// 営業日(朝5:00切替)にもとづく時刻の整形（ISSUE#135）。
//
// 深夜営業のため、0〜4時台は「前日の 24〜28 時」として読むのが業務上の正。
// CTI は運用当初からこの表記で統一されており（hurray: base/functions.ts の formatHM が
// h<5 なら +24、base/constraint.tsx の DATE_LINE=5）、AWS 側もこれに揃える。
//
// 各アプリで手書きしていたため 5時/6時/8時と基準が分裂していた経緯があるので、
// 時刻の表示はこのファイルの関数を通すこと。

// 営業日の切替時刻。バックエンドの commonConstants.BusinessDayOffsetHours および
// CTI の DATE_LINE と同じ値でなければならない。
export const BUSINESS_DAY_OFFSET_HOURS = 5

// 営業日1日の長さ（時）。5:00〜29:00 の24時間。
export const BUSINESS_DAY_HOURS = 24

// 営業日の中で表示できる最初と最後の「時」。
export const BUSINESS_DAY_FIRST_HOUR = BUSINESS_DAY_OFFSET_HOURS // 5
export const BUSINESS_DAY_LAST_HOUR = BUSINESS_DAY_OFFSET_HOURS + BUSINESS_DAY_HOURS // 29

/**
 * 暦日の「時」(0-23) を営業日の通し時間に直す。0〜4時は 24〜28時になる。
 * 5〜23時はそのまま。
 */
export function toBusinessHour(hour: number): number {
  return hour < BUSINESS_DAY_OFFSET_HOURS ? hour + 24 : hour
}

/**
 * "HH:mm" を営業日の表記に直す。"01:30" → "25:30"、"18:00" → "18:00"。
 * 解釈できない値は空文字を返す（呼び出し側で「未定」等にフォールバックできるように）。
 *
 * すでに 24 時以降で渡された場合（"25:30"）はそのまま返すので、
 * 変換済みの値を二重に足してしまう事故が起きない。
 */
export function formatBusinessTime(hhmm: string | undefined | null): string {
  if (!hhmm) return ""
  const [h, m] = hhmm.split(":")
  const hour = Number.parseInt(h, 10)
  const minute = Number.parseInt(m, 10)
  if (Number.isNaN(hour) || Number.isNaN(minute)) return ""
  const businessHour = toBusinessHour(hour)
  return `${String(businessHour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
}

/**
 * 営業日 0:00 起点の分を "HH:mm" に直す。1500 → "25:00"。
 * バックエンドが勤務窓などを分で返すため、その表示に使う。
 */
export function formatBusinessMinutes(minutes: number | undefined | null): string {
  if (minutes === undefined || minutes === null || Number.isNaN(minutes)) return ""
  const hour = Math.floor(minutes / 60)
  const minute = minutes % 60
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
}

/**
 * "HH:mm〜HH:mm" 形式の勤務時間帯を営業日表記に直す。
 * 区切りが見つからない場合（"通常出勤" のような非レンジ表記）はそのまま返す。
 */
export function formatBusinessTimeRange(range: string | undefined | null): string {
  if (!range) return ""
  const parts = range.split("〜")
  if (parts.length !== 2) return range
  const from = formatBusinessTime(parts[0])
  const to = formatBusinessTime(parts[1])
  if (!from || !to) return range
  return `${from}〜${to}`
}
